import { Stack, useTheme } from "@mui/material";
import { ReactNode, useMemo, useState } from "react";
import { DateRange } from "react-big-calendar";

import { ColorHelper } from "@/common/helpers/color";
import { DatetimeHelper } from "@/common/helpers/datetime";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { Mapper } from "@/common/mapping/mapper";
import { GenericCalendarEntryOfScheduleEntryDto } from "@/common/mapping/mappers/scheduleEntry";
import { apiClient } from "@/core/api/ApiClient";
import { VehicleDto } from "@/core/api/generated/v1";

import AppIconButton from "../../Button/AppIconButton";
import GeneralStatusHistoryInline from "../../Entity/GeneralStatus/GeneralStatusHistory/GeneralStatusHistoryInline";
import AppIcon from "../../Icons/AppIcon";
import AppPopover from "../../Popover/AppPopover";
import AppPopoverContent from "../../Popover/AppPopoverContent";
import { Calendar, CalendarProps } from "../Calendar";
import { CalendarEntryPopoverContent } from "../CalendarEntryPopoverContent";
import { CalendarExternalToolbar } from "../CalendarExternalToolbar";
import { ScheduleEntryDetails } from "../ScheduleEntryDetails";
import {
  VehicleStatusScheduleEntryFilters,
  VehicleStatusScheduleEntryFiltersInput,
} from "./VehicleStatusScheduleEntryFiltersInput";

interface Props {
  vehicle: VehicleDto;
  tabs: ReactNode;
  calendarProps: Partial<CalendarProps<GenericCalendarEntryOfScheduleEntryDto>>;
}

export function VehicleStatusHistoryCalendar({ vehicle, tabs, calendarProps }: Props) {
  const theme = useTheme();

  const [dateRange, setDateRange] = useState<DateRange>(() =>
    DatetimeHelper.getMonthRangeExclusive(calendarProps.defaultDate),
  );
  const [filters, setFilters] = useState<VehicleStatusScheduleEntryFilters | null>(null);

  const entriesRequest = useApiRequest(
    apiClient.vehicleScheduleApi.apiV1VehiclesVehicleIdScheduleEntriesStatusGetPost,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      vehicleId: vehicle.id || "",
      scheduleStatusEntriesGetDto: {
        range: {
          from: dateRange.start.toISOString(),
          to: dateRange.end.toISOString(),
        },
        statusIdentifiers: filters?.statusIdentifiers,
      },
    },
    {
      deps: [vehicle.id, dateRange, filters],
    },
  );
  const entries = entriesRequest.data?.entries;

  const genericEntries = useMemo(
    () =>
      entries?.map((x) => {
        const colorSet = ColorHelper.getAppSoftColorSetForString({
          str:
            x.sourceEntity?.generalStatusHistory?.statusIdentifier ||
            x.sourceEntity?.entityType?.toString() ||
            "",
          isDarkTheme: theme.palette.mode === "dark",
        });

        return Mapper.scheduleEntry.mapScheduleEntryDtoToGenericCalendarEntry(x, {
          style: {
            backgroundColor: colorSet.backgroundColorHsla.hsla,
            color: colorSet.textColorHsla.hsla,
          },
        });
      }) || [],
    [entries, theme.palette.mode],
  );

  return (
    <Stack gap={1}>
      <CalendarExternalToolbar
        isLoading={entriesRequest.isLoading}
        tabs={tabs}
        actions={
          <>
            <AppPopover
              clickBehavior={{}}
              trigger={
                <AppIconButton
                  tooltipProps={{ title: "Filters", enterDelay: 300, enterNextDelay: 300 }}
                >
                  <AppIcon of='filters' />
                </AppIconButton>
              }
            >
              {(props) => (
                <AppPopoverContent sx={{ width: { xxs: 250, md: 300, lg: 400 } }}>
                  <VehicleStatusScheduleEntryFiltersInput
                    defaultValue={filters}
                    onCancel={props.close}
                    onSave={(value) => {
                      setFilters(value);
                      props.close();
                    }}
                  />
                </AppPopoverContent>
              )}
            </AppPopover>
          </>
        }
        onRefetch={entriesRequest.refetch}
      />

      <Calendar
        {...calendarProps}
        entries={genericEntries}
        renderEntryTitle={(entry) => (
          <GeneralStatusHistoryInline
            entityId={entry.data.sourceEntity?.entity?.entityId}
            entity={entry.data.sourceEntity?.generalStatusHistory}
            withIcon
            withTooltip={false}
          />
        )}
        renderEntryPopoverContent={(params) => (
          <CalendarEntryPopoverContent sx={{ minWidth: { xxs: 200, md: 400 } }}>
            <ScheduleEntryDetails
              entry={params.entry.data}
              onSourceEntityUpdate={() => {
                entriesRequest.refetch();
              }}
              onSourceEntityDelete={() => {
                params.close();
                entriesRequest.refetch();
              }}
            />
          </CalendarEntryPopoverContent>
        )}
        onDateRangeChange={(newValue) => {
          setDateRange(newValue.dateRangeExclusive);
        }}
      />
    </Stack>
  );
}
